/** Day offsets for Today page DayFilterMenu (today + next 5 days). */
export const DAY_FILTER_OFFSETS = [0, 1, 2, 3, 4, 5] as const;

export type DayFilterOffset = (typeof DAY_FILTER_OFFSETS)[number];

/** Default selection: today. */
export const DEFAULT_DAY_FILTER_OFFSET: DayFilterOffset = 0;

export type DayFilterOption = {
  offset: DayFilterOffset;
  label: string;
  date: Date;
};

export type DayRange = {
  DateFrom: string;
  DateTo: string;
};

export type DayFilterLabelOptions = {
  locale?: string;
  todayLabel?: string;
  tomorrowLabel?: string;
};

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDayMonth(date: Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }).format(date);
}

function formatWeekday(date: Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
  }).format(date);
}

export function dayFilterLabel(
  offset: DayFilterOffset,
  date: Date,
  options: DayFilterLabelOptions = {},
): string {
  const dayMonth = formatDayMonth(date, options.locale);
  if (offset === 0) return options.todayLabel ?? 'Today';
  if (offset === 1) return `${options.tomorrowLabel ?? 'Tomorrow'}, ${dayMonth}`;
  return `${formatWeekday(date, options.locale)}, ${dayMonth}`;
}

/** Build the six day chips relative to `now` (defaults to current local time). */
export function buildDayFilterOptions(
  now: Date = new Date(),
  labelOptions: DayFilterLabelOptions = {},
): DayFilterOption[] {
  const today = startOfLocalDay(now);
  return DAY_FILTER_OFFSETS.map((offset) => {
    const date = addDays(today, offset);
    return {
      offset,
      label: dayFilterLabel(offset, date, labelOptions),
      date,
    };
  });
}

/**
 * Half-open local calendar range for the selected offset:
 * DateFrom = start of day, DateTo = start of next day (ISO).
 */
export function dayRange(offset: DayFilterOffset, now: Date = new Date()): DayRange {
  const from = addDays(startOfLocalDay(now), offset);
  const to = addDays(from, 1);
  return {
    DateFrom: from.toISOString(),
    DateTo: to.toISOString(),
  };
}
